import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";

/**
 * design-spec-animal-cards-2026-07-10.md §0 "Species-icon fallback" — the
 * 7-icon set (goat/pig/chicken/dog/horse/cat/generic) a card falls back to
 * when it has no photo, `--pine` on `--pine-soft`, never initials/a colored
 * dot. Paths are ported verbatim from the approved mockup's inline `<symbol>`
 * set (`deliverables/animal-cards-mockup-2026-07-10.html`) — the spec calls
 * these "usable placeholders... not final production art" and recommends
 * the Creative Director commission a matched icon set before ship; swapping
 * these paths later is a self-contained, single-file change.
 */
export type SpeciesIconKey = "goat" | "pig" | "chicken" | "dog" | "horse" | "cat" | "generic";

/** Freeform-string → icon-key matching. `animals.species` is a nullable
 * freeform string (existing rows already contain import artifacts like
 * "likely horse" — design-spec §5 CD blocking change #3), so this is
 * substring/keyword matching, never an exact-match enum lookup. First
 * match wins; unmatched or `null` species falls back to "generic" (the
 * mockup's "Unconfirmed species fallback" callout). */
const SPECIES_KEYWORDS: Array<[RegExp, SpeciesIconKey]> = [
  [/goat/i, "goat"],
  [/pig|hog|sow/i, "pig"],
  [/chick|hen|rooster/i, "chicken"],
  [/dog|pup/i, "dog"],
  [/horse|pony|mare|gelding|colt|foal/i, "horse"],
  [/cat|kitten/i, "cat"],
];

export function speciesIconKey(species: string | null): SpeciesIconKey {
  if (!species) return "generic";
  for (const [pattern, key] of SPECIES_KEYWORDS) {
    if (pattern.test(species)) return key;
  }
  return "generic";
}

function SpeciesIconSvg({ path }: { path: JSX.Element }) {
  return (
    <svg viewBox="0 0 64 64" focusable="false">
      {path}
    </svg>
  );
}

const ICON_PATHS: Record<SpeciesIconKey, JSX.Element> = {
  goat: (
    <>
      <ellipse cx="32" cy="38" rx="15" ry="13" fill="currentColor" />
      <path d="M19 28c-5-10 2-16 8-10-2 4-4 8-4 12z" fill="currentColor" />
      <path d="M45 28c5-10-2-16-8-10 2 4 4 8 4 12z" fill="currentColor" />
      <ellipse cx="18" cy="40" rx="4" ry="6" fill="currentColor" />
      <ellipse cx="46" cy="40" rx="4" ry="6" fill="currentColor" />
      <path d="M27 49l5 9 5-9z" fill="currentColor" />
    </>
  ),
  pig: (
    <>
      <ellipse cx="32" cy="34" rx="17" ry="15" fill="currentColor" />
      <ellipse cx="19" cy="23" rx="6" ry="7" fill="currentColor" />
      <ellipse cx="45" cy="23" rx="6" ry="7" fill="currentColor" />
      <ellipse cx="32" cy="41" rx="8" ry="6" fill="currentColor" opacity="0.55" />
    </>
  ),
  chicken: (
    <>
      <ellipse cx="30" cy="40" rx="16" ry="13" fill="currentColor" />
      <circle cx="42" cy="24" r="9" fill="currentColor" />
      <path d="M50 22l8 3-8 4z" fill="currentColor" />
      <path d="M38 15c2-6 8-6 9-1-3 0-6 1-9 1z" fill="currentColor" />
      <path d="M20 46l-10 4 6 5z" fill="currentColor" />
    </>
  ),
  dog: (
    <>
      <ellipse cx="32" cy="36" rx="16" ry="14" fill="currentColor" />
      <path d="M16 24c-6-2-8 8-4 14 3-2 5-6 4-14z" fill="currentColor" />
      <path d="M48 24c6-2 8 8 4 14-3-2-5-6-4-14z" fill="currentColor" />
      <ellipse cx="32" cy="42" rx="7" ry="5" fill="currentColor" opacity="0.55" />
    </>
  ),
  horse: (
    <>
      <path
        d="M22 50c-2-10 0-24 10-30 8-4 16 0 18 8 2 6-2 10-6 10-2 6 0 10 2 12z"
        fill="currentColor"
      />
      <path d="M28 14c6-4 12-2 14 2-4 2-9 2-14-2z" fill="currentColor" />
      <ellipse cx="24" cy="44" rx="5" ry="8" fill="currentColor" />
    </>
  ),
  cat: (
    <>
      <ellipse cx="32" cy="38" rx="15" ry="13" fill="currentColor" />
      <path d="M19 26l-3-12 12 7z" fill="currentColor" />
      <path d="M45 26l3-12-12 7z" fill="currentColor" />
    </>
  ),
  generic: (
    <>
      <circle cx="32" cy="34" r="16" fill="currentColor" />
      <ellipse cx="18" cy="26" rx="5" ry="6" fill="currentColor" />
      <ellipse cx="46" cy="26" rx="5" ry="6" fill="currentColor" />
    </>
  ),
};

export interface AnimalAvatarProps {
  /** `animals.species` — a nullable freeform string; matched to an icon
   * key via `speciesIconKey()`. Ignored once `photoUrl` is set (and
   * renders instead, without a remount, if the photo `<img>` fails to
   * load — see the doc comment below). */
  species: string | null;
  /**
   * Sprint-013 B2 — `AnimalsView`/`DashboardView` derive this from
   * `animal.primary_photo_attachment_id` via `attachmentThumbUrl()`
   * (`../lib/api`); `undefined` (no resolved photo) falls back to the
   * species silhouette below.
   */
  photoUrl?: string;
}

/**
 * design-spec-animal-cards-2026-07-10.md §2 (Option B) + the Creative
 * Director's blocking change #1 (2026-07-10): the photo case renders a
 * real `<img>` — `56×56px`, `object-fit: cover`, `object-position:
 * center`, `alt=""` (decorative; the card's own composed `aria-label` is
 * the accessible name — a non-empty alt would double-announce), plus a
 * hardcoded inset hairline so a light-edged photo doesn't bleed into the
 * white card face. The no-photo fallback (species silhouette on
 * `--pine-soft`) occupies the IDENTICAL 56×56px box — same class/
 * dimensions, only the fill differs — so photo and no-photo rows align
 * pixel-for-pixel.
 *
 * Sprint-013 B2 — a broken/deleted attachment (the resolved
 * `primary_photo_attachment_id` pointed at a photo that's since been
 * removed, or a network hiccup on the thumbnail request) falls back to
 * the SAME silhouette on the `<img>`'s `onError`, never a broken-image
 * icon. The failed-flag resets whenever `photoUrl` itself changes (a
 * different animal, or a freshly set/cleared card photo) so a stale
 * failure never sticks to a URL that might succeed.
 */
export function AnimalAvatar({ species, photoUrl }: AnimalAvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    setImgFailed(false);
  }, [photoUrl]);

  if (photoUrl && !imgFailed) {
    return (
      // alt="" is intentional here — see doc comment above.
      <img
        class="animal-card-avatar animal-card-avatar-photo"
        src={photoUrl}
        alt=""
        width={56}
        height={56}
        onError={() => setImgFailed(true)}
      />
    );
  }
  const key = speciesIconKey(species);
  return (
    <span class="animal-card-avatar animal-card-avatar-fallback" aria-hidden="true">
      <SpeciesIconSvg path={ICON_PATHS[key]} />
    </span>
  );
}
